import { useState, useEffect } from 'react'
import FileUpload from './components/FileUpload'
import MapView from './components/MapView'
import DataTable from './components/DataTable'
import { processFilesAsync } from './utils/asyncProcessor'
import { validateS3Config } from './config/s3Config'

function App() {
  const [data, setData] = useState(null)
  const [leaks, setLeaks] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0, currentFile: '' })
  const [selectedPoint, setSelectedPoint] = useState(null)
  const [expandTableRef, setExpandTableRef] = useState(null)
  const [uploadResults, setUploadResults] = useState([])
  const [s3Enabled, setS3Enabled] = useState(() => {
    // Initialize S3 state immediately to avoid flash of development message
    const s3Validation = validateS3Config()
    return s3Validation.isValid
  })

  // Check S3 configuration on app startup
  useEffect(() => {
    const s3Validation = validateS3Config()
    setS3Enabled(s3Validation.isValid)
    // Debug logging (disabled in production)
    if (import.meta.env.DEV) {
      console.log('App startup S3 check:', {
        isValid: s3Validation.isValid,
        errors: s3Validation.errors,
        reason: s3Validation.reason
      })
    }
  }, [])

  const handleFileUpload = async (files) => {
    setLoading(true)
    setError(null)
    setProcessingProgress({ current: 0, total: 0, currentFile: '' })
    
    try {
      // Handle both single file and multiple files
      const fileArray = Array.isArray(files) ? files : [files]
      
      console.log(`Processing ${fileArray.length} file(s):`, fileArray.map(f => f.name))
      
      // Process files asynchronously with progress updates
      const result = await processFilesAsync(
        fileArray,
        // File progress callback
        (progress) => {
          setProcessingProgress({
            current: progress.current,
            total: progress.total,
            currentFile: progress.currentFile
          })
        },
        // Data progress callback
        (progress) => {
          setProcessingProgress(prev => ({
            ...prev,
            stage: progress.stage || 'Processing data...',
            dataProgress: progress
          }))
        }
      )
      
      // Re-assign IDs to avoid conflicts
      const finalData = result.data.map((point, index) => ({
        ...point,
        id: index
      }))
      
      // Debug logging (disabled in production)
      if (import.meta.env.DEV) {
        console.log('App: Processing result:', {
          dataLength: finalData.length,
          leaksLength: result.leaks.length,
          uploadResults: result.uploadResults,
          s3Enabled: result.s3Enabled
        })
      }
      
      setData(finalData)
      setLeaks(result.leaks)
      setUploadResults(result.uploadResults || [])
      setS3Enabled(result.s3Enabled || false)
      
      console.log(`Combined data: ${finalData.length} points, ${result.leaks.length} leaks from ${fileArray.length} files`)
      if (result.uploadResults) {
        console.log('Upload results:', result.uploadResults)
      }
      
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      setProcessingProgress({ current: 0, total: 0, currentFile: '' })
    }
  }

  const handleReset = () => {
    setData(null)
    setLeaks([])
    setError(null)
    setLoading(false)
    setSelectedPoint(null)
    setUploadResults([])
    // Don't reset s3Enabled - it should stay based on environment
  }

  const handlePointSelect = (point) => {
    setSelectedPoint(point)
  }

  const handleShowInTable = (point) => {
    setSelectedPoint(point)
    if (expandTableRef) {
      expandTableRef()
    }
  }

  const handleExpandTableRef = (expandFn) => {
    setExpandTableRef(() => expandFn)
  }

  const handleTableRowClick = (point) => {
    setSelectedPoint(point)
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              {data && (
                <button
                  onClick={handleReset}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>New File</span>
                </button>
              )}
              <h1 className="text-xl font-semibold text-gray-900">
                Methane Detection Viewer
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {data && (
                <div className="text-sm text-gray-600">
                  {data.length.toLocaleString()} measurements
                  {leaks.length > 0 && (
                    <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                      {leaks.length} potential leaks
                    </span>
                  )}
                  {(() => {
                    const uniqueFiles = [...new Set(data.map(point => point.sourceFile))]
                    if (uniqueFiles.length > 1) {
                      return (
                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {uniqueFiles.length} files
                        </span>
                      )
                    }
                    return null
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex overflow-hidden">
        {!data ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <FileUpload 
              onFileUpload={handleFileUpload}
              loading={loading}
              error={error}
              processingProgress={processingProgress}
              uploadResults={uploadResults}
              s3Enabled={s3Enabled}
            />
          </div>
        ) : (
          <div className="flex-1 flex flex-col">
            {/* Map View */}
            <div className="flex-1 min-h-0">
              <MapView data={data} leaks={leaks} onPointSelect={handlePointSelect} onShowInTable={handleShowInTable} selectedPoint={selectedPoint} />
            </div>
            
            {/* Data Table (Collapsible) */}
            <div className="bg-white border-t">
              <DataTable data={data} leaks={leaks} selectedPoint={selectedPoint} onPointSelect={handlePointSelect} onExpandTable={handleExpandTableRef} onTableRowClick={handleTableRowClick} />
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default App
